// ML001-F0252..F0255 (WebUI #170): the customer's monthly view on their own account. The draft invoice of everything unbilled
// (previous month and older) with the line "If you're happy it will be issued", "I'm happy, please issue" (draft.customer_issue, pinned to
// the draft version the customer SAW) and "Not happy? Message us" (draft.not_happy, pinned the same way); and the current-month advice
// list, where only CURRENT-month items can be advanced into the draft (transaction.advance). Nothing is decided here: the Ledger refuses,
// and every refusal is shown verbatim (refusalText). Ledger design ML001-D013 section 2.64.
// Wire (Spec core-ledger-account-gateway 1.12.0, final, Spec main 3f14499a): the customer's draft comes from draft.customer_get, the one read that
// carries Draft.version. That version is the expected_version draft.customer_issue and draft.not_happy confirm. Issue is disabled while the
// version is not the one the customer has reviewed (stale), and when the draft carries no valid version. Nothing falls back to another read.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { monthlyCycleApi } from '../../services/monthlyCycleApi';
import { customerBillingProfileApi } from '../../services/customerBillingProfileApi';
import DraftGroupSwitcher from './DraftGroupSwitcher';
import { SELECTOR_SENTENCE, billToName, draftsOf, isSelectorRequired, selectorFor, shareText } from './draftGroups';

// M004-F0799 (lazy): a draft read that answers 409 SELECTOR_REQUIRED resolves to this marker, so the load can re-read draft.list and show the switcher.
const SELECTOR_REQUIRED_READ = Symbol('selector-required-read');
import { classifyRelayOutcome } from '../../services/relayOutcome';
import { refusalText } from './ledgerRefusal';
import { canManageCreditNotes } from './creditNoteModel';
import './CreditNotesPanel.css';

const londonMonth = (date = new Date()) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit' }).format(date);
const monthOf = (day) => String(day || '').slice(0, 7);
const HEX64 = /^[0-9a-f]{64}$/;
const money = (minor) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(minor || 0) / 100);
// Features: M004-F0775 (WebUI #248). An unknown outcome (timeout, 502/503/504, no response) is never shown as a failure: the Ledger may have applied it.
const UNCONFIRMED_TAIL = "We've refreshed what the Ledger shows — check it before trying again.";
const UNCONFIRMED = {
  issue: `We couldn't confirm whether your invoice was issued. ${UNCONFIRMED_TAIL}`,
  notHappy: `We couldn't confirm whether your message was sent. ${UNCONFIRMED_TAIL}`,
  advance: `We couldn't confirm whether this was added to your invoice. ${UNCONFIRMED_TAIL}`,
};

// The draft as the customer read it (draft.customer_get: { binding_generation, draft } with draft null or a Draft): the version is what
// the customer is confirming, the lines are what they see. A version that is not a sha256 hex string is not a version: Issue stays off.
export function draftFromRead(payload) {
  const draft = payload?.draft && typeof payload.draft === 'object' ? payload.draft : null;
  if (!draft?.invoice_id) return null;
  const lines = Array.isArray(draft.lines) ? draft.lines : [];
  return {
    invoiceId: String(draft.invoice_id),
    version: typeof draft.version === 'string' && HEX64.test(draft.version) ? draft.version : null,
    lines: lines.map((line) => ({ id: String(line.line_id || line.hand_off_id || line.description), description: line.description || '—', amount_minor: line.amount_minor, share: line.share_of || null })),
    total_minor: Number(draft.total_minor ?? 0), // Draft.total_minor on the wire
    groupLabel: typeof draft.group_label === 'string' ? draft.group_label : '',
    billTo: billToName(draft), // the bill-to display name only (design §9.7)
  };
}

// The advice items of the current month (AdviceItem on the wire). The id is the hand-off id, which is the only item Advance can move.
export function adviceFromRead(payload) {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return items.map((item) => ({
    id: String(item.hand_off_id || item.advice_id || ''),
    handOffId: item.hand_off_id ? String(item.hand_off_id) : null,
    date: String(item.date || '').slice(0, 10),
    description: item.description || '—',
    amount_minor: item.amount_minor ?? 0,
  })).filter((item) => item.id);
}

export default function CustomerMonthlyDraft({ orgId, userId, permissions }) {
  const staffOnOwnAccount = canManageCreditNotes(permissions); // a billing manager who is also the customer: the Ledger refuses, so no Issue control is offered
  const [draft, setDraft] = useState(null);
  const [seenVersion, setSeenVersion] = useState(null); // the version the customer has reviewed; Issue and Not happy pin to it
  const [advice, setAdvice] = useState([]);
  const [cycle, setCycle] = useState(londonMonth());
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');
  const [unconfirmed, setUnconfirmed] = useState(''); // Features: M004-F0775
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(false);
  const [notHappyOpen, setNotHappyOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messageError, setMessageError] = useState('');
  // M004-F0799: the open drafts (draft.list) and the chosen one. The chosen invoice_id goes on every draft op when there are several.
  const [drafts, setDrafts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const selectedRef = useRef(null);
  const [selectorSentence, setSelectorSentence] = useState('');

  // mode: 'initial' takes the first version as the one reviewed; 'acknowledge' takes the new version after the customer's own change
  // (advance) they can see on screen; 'refresh' keeps the reviewed version, so a changed draft is stale until the customer reviews it.
  const readDrafts = useCallback(async () => draftsOf((await customerBillingProfileApi.draftList(orgId, userId))?.data?.data), [orgId, userId]);
  // M004-F0799 (lazy): the drafts list is kept here once a 409 SELECTOR_REQUIRED has read it; a draft read sends the chosen invoice_id only then.
  const draftsRef = useRef([]);

  // Choosing a draft is a selection, not a save: the chosen draft is shown as read (its version is then the one reviewed).
  const choose = (invoiceId) => {
    selectedRef.current = invoiceId;
    setSelectedId(invoiceId);
    setSelectorSentence('');
    return load('initial', invoiceId);
  };

  // A 409 LEDGER_DRAFT_SELECTOR_REQUIRED: the list is re-read, the switcher shows with the Ledger's sentence, and the customer chooses.
  const reselect = useCallback(async () => {
    const list = await readDrafts();
    draftsRef.current = list;
    setDrafts(list);
    setDraft(null);
    selectedRef.current = null;
    setSelectedId(null);
    setSelectorSentence(SELECTOR_SENTENCE);
  }, [readDrafts]);

  // mode: 'initial' takes the first version as the one reviewed; 'acknowledge' takes the new version after the customer's own change
  // (advance) they can see on screen; 'refresh' keeps the reviewed version, so a changed draft is stale until the customer reviews it.
  const load = useCallback(async (mode = 'refresh', choice = selectedRef.current) => {
    setLoadError('');
    try {
      // M004-F0799 (lazy): the draft is read WITHOUT a selector, exactly as on main. draft.list is read only after a 409 SELECTOR_REQUIRED.
      const selector = selectorFor(draftsRef.current, choice);
      const draftRead = monthlyCycleApi.draftCustomerGet(orgId, userId, ...(selector.invoiceId ? [selector] : [])).catch((cause) => {
        if (isSelectorRequired(cause)) return SELECTOR_REQUIRED_READ;
        throw cause;
      });
      const [draftResponse, adviceRelay] = await Promise.all([draftRead, monthlyCycleApi.adviceCurrent(orgId, userId)]);
      setAdvice(adviceFromRead(adviceRelay));
      setCycle(adviceRelay?.cycle || londonMonth());
      if (draftResponse === SELECTOR_REQUIRED_READ) { await reselect(); return; }
      const next = draftFromRead(draftResponse);
      setDraft(next);
      if (mode !== 'refresh') setSeenVersion(next?.version || null);
    } catch (cause) {
      setLoadError(refusalText(cause, 'Your monthly statement could not be loaded.'));
    }
  }, [orgId, userId, reselect]);

  useEffect(() => { load('initial'); }, [load]);

  const versionKnown = Boolean(draft?.version);
  const reviewed = versionKnown && draft.version === seenVersion && !stale;
  const issueEnabled = reviewed && !busy && !staffOnOwnAccount;

  const issue = async () => {
    if (!issueEnabled) return;
    setBusy(true); setActionError(''); setNotice(''); setUnconfirmed('');
    try {
      const selector = selectorFor(drafts, selectedRef.current);
      await monthlyCycleApi.customerIssue(orgId, userId, seenVersion, ...(selector.invoiceId ? [selector] : []));
      setNotice('Your invoice has been issued.');
      await load();
    } catch (cause) {
      if (isSelectorRequired(cause)) {
        await reselect();
      } else if (classifyRelayOutcome(cause) === 'unknown') {
        // Features: M004-F0775 - no "Nothing was issued"; re-read the draft the view already loads.
        setUnconfirmed(UNCONFIRMED.issue);
        await load();
      } else {
        setActionError(refusalText(cause, 'The invoice could not be issued. Nothing was issued.'));
        if (cause?.response?.data?.error?.code === 'LEDGER_DRAFT_CHANGED') {
          // The shown draft is no longer the one on the Ledger: read it again, and keep Issue off until the customer reviews it.
          setStale(true);
          await load();
        }
      }
    } finally { setBusy(false); }
  };

  const reviewUpdate = async () => {
    setStale(false);
    setSeenVersion(draft?.version || null);
    setNotice('Review the updated draft, then confirm.');
  };

  const sendNotHappy = async (event) => {
    event.preventDefault();
    const text = message.trim();
    if (!text) { setMessageError('Tell us what is wrong with the draft.'); return; }
    setMessageError(''); setBusy(true); setActionError(''); setNotice(''); setUnconfirmed('');
    try {
      const selector = selectorFor(drafts, selectedRef.current);
      await monthlyCycleApi.notHappy(orgId, userId, seenVersion, text, ...(selector.invoiceId ? [selector] : []));
      setNotice('Thanks. We have your message and will not issue this draft until we have talked.');
      setNotHappyOpen(false); setMessage('');
    } catch (cause) {
      if (isSelectorRequired(cause)) {
        await reselect();
      } else if (classifyRelayOutcome(cause) === 'unknown') {
        // Features: M004-F0775 - the form stays open with the text; the draft is re-read (the view's own load).
        setUnconfirmed(UNCONFIRMED.notHappy);
        await load();
      } else {
        setActionError(refusalText(cause, 'Your message could not be sent. Nothing was changed.'));
      }
    } finally { setBusy(false); }
  };

  const advance = async (item) => {
    setBusy(true); setActionError(''); setNotice(''); setUnconfirmed('');
    try {
      await monthlyCycleApi.advance(orgId, userId, item.handOffId);
      setNotice('Added to your invoice.');
      await load('acknowledge');
    } catch (cause) {
      if (classifyRelayOutcome(cause) === 'unknown') {
        // Features: M004-F0775 - re-read in 'acknowledge' mode, as the success path does, so the Ledger's draft is shown.
        setUnconfirmed(UNCONFIRMED.advance);
        await load('acknowledge');
      } else {
        setActionError(refusalText(cause, 'This could not be added to your invoice. Nothing was changed.'));
      }
    } finally { setBusy(false); }
  };

  return (
    <section className="cn-panel cm-monthly" data-testid="monthly-cycle" aria-label="Your monthly statement">
      <header className="cn-panel-head"><div><h3>Your monthly statement</h3></div></header>
      {notice ? <p className="cn-notice" role="status" data-testid="monthly-notice">{notice}</p> : null}
      {actionError ? <p className="cn-form-error" role="alert" data-testid="monthly-error">{actionError}</p> : null}
      {unconfirmed ? <p className="cn-notice" role="status" data-testid="monthly-unconfirmed">{unconfirmed}</p> : null}
      {loadError ? <p className="cn-form-error" role="alert" data-testid="monthly-load-error">{loadError}</p> : null}

      <DraftGroupSwitcher drafts={drafts} value={selectedId} onChange={choose} sentence={selectorSentence} />

      {draft ? (
        <div className="cm-draft" data-testid="monthly-draft">
          <h4>Draft invoice</h4>
          {draft.groupLabel ? <p data-testid="monthly-group">{draft.groupLabel}</p> : null}
          {draft.billTo ? <p data-testid="monthly-bill-to">Bill to: {draft.billTo}</p> : null}
          <p className="cn-muted" data-testid="monthly-happy-line">If you're happy it will be issued.</p>
          <table className="cn-table" data-testid="monthly-draft-lines">
            <tbody>
              {draft.lines.map((line) => (
                <tr key={line.id}>
                  <td>{line.description}{line.share ? <span data-testid="monthly-share"> ({shareText(line.share)})</span> : null}</td>
                  <td>{money(line.amount_minor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p data-testid="monthly-total">Total {money(draft.total_minor)}</p>
          {staffOnOwnAccount ? (
            <p className="cn-muted" data-testid="monthly-own-account-hint">A different billing manager must issue your invoice.</p>
          ) : null}
          {stale ? (
            <p className="cn-form-error" role="alert" data-testid="monthly-stale">The draft has changed since you read it.<button type="button" data-testid="monthly-review" onClick={reviewUpdate}>I have reviewed the updated draft</button></p>
          ) : null}
          {!staffOnOwnAccount ? (
            <div className="cn-actions">
              <button type="button" className="cn-primary" data-testid="monthly-issue" disabled={!issueEnabled} onClick={issue}>I&apos;m happy, please issue</button>
              <button type="button" data-testid="monthly-not-happy-open" onClick={() => setNotHappyOpen(true)} disabled={busy || !versionKnown}>Not happy? Message us</button>
            </div>
          ) : null}
          {!versionKnown ? <p className="cn-muted" data-testid="monthly-version-missing">Issuing is off until this draft carries its version from the Ledger.</p> : null}
          {notHappyOpen ? (
            <form className="cn-form" onSubmit={sendNotHappy} noValidate data-testid="monthly-not-happy-form">
              <label>Message<textarea data-testid="monthly-not-happy-text" value={message} onChange={(event) => setMessage(event.target.value)} aria-invalid={Boolean(messageError)} /></label>
              {messageError ? <small className="cn-field-error" role="alert">{messageError}</small> : null}
              <div className="cn-actions">
                <button type="button" onClick={() => setNotHappyOpen(false)}>Back</button>
                <button type="submit" className="cn-primary" data-testid="monthly-not-happy-send" disabled={busy}>Send message</button>
              </div>
            </form>
          ) : null}
        </div>
      ) : <p className="cn-empty" data-testid="monthly-no-draft">No draft invoice is open.</p>}

      <div className="cm-advice" data-testid="monthly-advice">
        <h4>Advice for {cycle}</h4>
        {advice.length ? (
          <ul data-testid="monthly-advice-list">
            {advice.map((item) => {
              const current = monthOf(item.date) === cycle;
              return (
                <li key={item.id} data-testid="monthly-advice-item">
                  <span>{item.description} · {item.date}</span> <span>{money(item.amount_minor)}</span>
                  {current && item.handOffId ? <button type="button" data-testid="monthly-advance" onClick={() => advance(item)} disabled={busy}>Advance into this invoice</button> : null}
                </li>
              );
            })}
          </ul>
        ) : <p className="cn-muted" data-testid="monthly-advice-empty">Nothing is in advice for this month.</p>}
      </div>
    </section>
  );
}
